import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Send, Pause, Play, UserPlus, Loader2, Eye, Check, X, Clock, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { admin } from "@/lib/api";
import { API } from "@/lib/api";
import { cn } from "@/lib/utils";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const STATUS_TONE = { active: "text-mk-lime border-mk-lime/40 bg-mk-lime/10", completed: "text-[#7fb0ff] border-mk-blue/40 bg-mk-blue/10", paused: "text-amber-300 border-amber-400/40 bg-amber-400/10", unsubscribed: "text-mk-muted border-mk-border bg-mk-bg" };

const Stat = ({ label, value, hint, testId }) => (
  <div className="rounded-2xl border border-mk-border bg-mk-surface p-5" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-mk-muted font-mono">{label}</div>
    <div className="mt-2 font-display text-3xl font-bold text-mk-text tabular-nums">{value}</div>
    {hint && <div className="mt-1 text-xs text-mk-dim">{hint}</div>}
  </div>
);

const Funnel = ({ steps }) => (
  <div className="rounded-2xl border border-mk-border bg-mk-surface p-5" data-testid="drip-funnel">
    <div className="flex items-center justify-between"><div className="font-display font-semibold text-mk-text">Step funnel</div><div className="text-xs text-mk-muted">sent → opened (tracking pixel)</div></div>
    <div className="mt-4 space-y-3">
      {steps.map((s) => {
        const max = Math.max(1, ...steps.map((x) => x.delivered));
        return (
          <div key={s.step} data-testid={`drip-funnel-step-${s.step}`}>
            <div className="flex items-center justify-between text-xs"><span className="text-mk-text"><span className="font-mono text-mk-lime mr-2">0{s.step}</span>{s.title}</span><span className="text-mk-dim tabular-nums">{s.delivered} sent · {s.opened} opened · <span className={cn(s.open_rate != null && s.open_rate >= 40 ? "text-mk-lime" : "text-mk-text")}>{s.open_rate == null ? "—" : `${s.open_rate}%`}</span></span></div>
            <div className="mt-1.5 h-2 rounded-full bg-mk-bg overflow-hidden relative">
              <div className="absolute inset-y-0 left-0 rounded-full bg-mk-blue/60" style={{ width: `${(s.delivered / max) * 100}%` }} />
              <div className="absolute inset-y-0 left-0 rounded-full bg-mk-lime" style={{ width: `${(s.opened / max) * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Dots = ({ item, onResend, busyKey }) => {
  const sentSteps = new Map();
  (item.sent || []).forEach((e) => { const prev = sentSteps.get(e.step); if (!prev || e.at > prev.at) sentSteps.set(e.step, e); });
  const opened = new Set((item.opens || []).map((o) => o.step));
  return (
    <div className="flex items-center gap-1.5" data-testid={`drip-dots-${item.email}`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const e = sentSteps.get(i);
        const state = opened.has(i) ? "opened" : e ? (e.ok ? (e.resend ? "resent" : "sent") : "failed") : item.next_step === i && item.status === "active" ? "next" : "pending";
        const key = `${item.email}:${i + 1}`;
        return (
          <button key={i} type="button" title={`Step ${i + 1}: ${state}${e ? ` · ${fmt(e.at)}` : ""} — click to (re)send now`} onClick={() => onResend(item.email, i + 1)} disabled={busyKey === key} data-testid={`drip-step-${i + 1}-${item.email}`}
            className={cn("grid h-7 w-7 place-items-center rounded-md border text-[10px] font-mono transition-colors hover:border-mk-lime/60",
              state === "opened" && "border-mk-lime bg-mk-lime text-mk-bg", (state === "sent" || state === "resent") && "border-mk-blue/60 bg-mk-blue/20 text-[#9cc1ff]", state === "failed" && "border-red-500/60 bg-red-500/10 text-red-300",
              state === "next" && "border-mk-lime/50 border-dashed text-mk-lime", state === "pending" && "border-mk-border text-mk-muted")}>
            {busyKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : state === "opened" ? <MailOpen className="h-3 w-3" /> : state === "sent" || state === "resent" ? <Check className="h-3 w-3" /> : state === "failed" ? <X className="h-3 w-3" /> : i + 1}
          </button>
        );
      })}
    </div>
  );
};

const EnrolForm = ({ onDone }) => {
  const [f, setF] = useState({ email: "", name: "", company: "", send_first_now: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await admin.dripEnroll({ ...f, source: "manual" }); toast.success(`Enrolled ${f.email}`); setF({ email: "", name: "", company: "", send_first_now: false }); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't enrol"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-mk-border bg-mk-surface p-5 grid sm:grid-cols-[1.4fr_1fr_1fr_auto] gap-2 items-end" data-testid="drip-enrol-form">
      <label className="text-xs text-mk-dim">Email<input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-dark-sm mt-1" placeholder="ops@company.com" data-testid="drip-enrol-email" /></label>
      <label className="text-xs text-mk-dim">Name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-dark-sm mt-1" placeholder="Alex" data-testid="drip-enrol-name" /></label>
      <label className="text-xs text-mk-dim">Company<input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="input-dark-sm mt-1" placeholder="Acme FM" data-testid="drip-enrol-company" /></label>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-mk-dim whitespace-nowrap"><input type="checkbox" checked={f.send_first_now} onChange={(e) => setF({ ...f, send_first_now: e.target.checked })} className="accent-[#d6ef37]" data-testid="drip-enrol-now" />Step 1 now</label>
        <button type="submit" disabled={busy} className="btn-lime !py-2 !px-4 text-sm" data-testid="drip-enrol-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" />Enrol</>}</button>
      </div>
    </form>
  );
};

export default function DripDashboard() {
  const [stats, setStats] = useState(null);
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const load = useCallback(async () => {
    const [s, l] = await Promise.all([admin.dripStats(), admin.dripEnrollments(filter || undefined)]);
    setStats(s); setList(l.items);
  }, [filter]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load drip data")); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusyKey(key);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    finally { setBusyKey(null); }
  };
  const resend = (email, step) => { if (window.confirm(`Send step ${step} to ${email} now?`)) act(`${email}:${step}`, () => admin.dripResend(email, step), `Step ${step} sent to ${email}`); };

  return (
    <div className="space-y-6" data-testid="drip-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Active" value={stats?.counts?.active ?? "—"} hint="in sequence" testId="drip-stat-active" />
        <Stat label="Completed" value={stats?.counts?.completed ?? "—"} hint="all 5 steps sent" testId="drip-stat-completed" />
        <Stat label="Open rate" value={stats?.overall_open_rate == null ? "—" : `${stats.overall_open_rate}%`} hint="unique opens / delivered" testId="drip-stat-open-rate" />
        <Stat label="Unsubscribed / paused" value={`${stats?.counts?.unsubscribed ?? 0} / ${stats?.counts?.paused ?? 0}`} testId="drip-stat-unsub" />
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4">
        {stats && <Funnel steps={stats.steps} />}
        <div className="rounded-2xl border border-mk-border bg-mk-surface p-5" data-testid="drip-steps-preview">
          <div className="font-display font-semibold text-mk-text">The sequence</div>
          <ul className="mt-3 space-y-2 text-sm">
            {(stats?.steps || []).map((s) => (
              <li key={s.step} className="flex items-center gap-3"><span className="font-mono text-xs text-mk-lime">0{s.step}</span><span className="flex-1 text-mk-dim truncate">{s.title}</span><a href={`${API}/drip/preview/${s.step}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-mk-text hover:text-mk-lime" data-testid={`drip-preview-${s.step}`}><Eye className="h-3.5 w-3.5" />Preview</a></li>
            ))}
          </ul>
        </div>
      </div>

      <EnrolForm onDone={load} />

      <div className="rounded-2xl border border-mk-border bg-mk-surface overflow-hidden" data-testid="drip-table">
        <div className="flex flex-wrap items-center gap-3 border-b border-mk-border px-5 py-3">
          <div className="font-display font-semibold text-mk-text">Enrolments</div>
          <div className="flex gap-1 rounded-full border border-mk-border bg-mk-bg p-0.5">
            {["", "active", "completed", "paused", "unsubscribed"].map((s) => (
              <button key={s} type="button" onClick={() => setFilter(s)} data-testid={`drip-filter-${s || "all"}`} className={cn("rounded-full px-3 py-1 text-xs capitalize", filter === s ? "bg-mk-raised text-mk-text" : "text-mk-muted hover:text-mk-text")}>{s || "all"}</button>
            ))}
          </div>
          <button type="button" onClick={() => load()} className="ml-auto btn-ghost !py-1.5 !px-3 text-xs" data-testid="drip-refresh"><RefreshCw className="h-3.5 w-3.5" />Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-mk-muted font-mono">
              <tr className="border-b border-mk-border"><th className="px-5 py-3">Person</th><th className="px-3 py-3">Steps</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Next</th><th className="px-3 py-3">Last open</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list === null && <tr><td colSpan={6} className="px-5 py-8 text-center text-mk-muted"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {list?.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-mk-muted" data-testid="drip-empty">Nobody in this view yet.</td></tr>}
              {list?.map((it) => (
                <tr key={it.email} className="border-b border-mk-border/60 hover:bg-mk-raised/30" data-testid={`drip-row-${it.email}`}>
                  <td className="px-5 py-3"><div className="font-medium text-mk-text">{it.name || "—"} <span className="text-mk-muted">·</span> <span className="text-mk-dim">{it.company || "—"}</span></div><div className="text-xs text-mk-muted">{it.email} · {it.source} · enrolled {fmt(it.enrolled_at)}</div></td>
                  <td className="px-3 py-3"><Dots item={it} onResend={resend} busyKey={busyKey} /></td>
                  <td className="px-3 py-3"><span className={cn("rounded-full border px-2.5 py-0.5 text-[11px] capitalize", STATUS_TONE[it.status])} data-testid={`drip-status-${it.email}`}>{it.status}</span></td>
                  <td className="px-3 py-3 text-xs text-mk-dim whitespace-nowrap">{it.status === "active" && it.next_step != null ? <><Clock className="inline h-3 w-3 mr-1" />Step {it.next_step + 1} · {fmt(it.next_due_at)}</> : "—"}</td>
                  <td className="px-3 py-3 text-xs text-mk-dim whitespace-nowrap">{it.last_open_at ? fmt(it.last_open_at) : "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1.5">
                      {it.status === "active" && it.next_step != null && <button type="button" onClick={() => act(`${it.email}:next`, () => admin.dripSendNext(it.email), `Step ${it.next_step + 1} sent`)} className="btn-ghost !py-1 !px-2.5 text-xs" data-testid={`drip-send-next-${it.email}`}><Send className="h-3 w-3" />Send next</button>}
                      {it.status === "active" && <button type="button" onClick={() => act(`${it.email}:pause`, () => admin.dripStatus(it.email, "paused"), "Paused")} className="btn-ghost !py-1 !px-2.5 text-xs" data-testid={`drip-pause-${it.email}`}><Pause className="h-3 w-3" /></button>}
                      {it.status === "paused" && <button type="button" onClick={() => act(`${it.email}:resume`, () => admin.dripStatus(it.email, "active"), "Resumed")} className="btn-ghost !py-1 !px-2.5 text-xs" data-testid={`drip-resume-${it.email}`}><Play className="h-3 w-3" />Resume</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
